import { useMemo, useState } from "react";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import Breadcrumb from "@/components/base/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import type { NewsItem } from "@/types";

const news = newsList as NewsItem[];
const PAGE_SIZE = 4;
const categories = ["全部", ...Array.from(new Set(news.map((n) => n.category)))];

export default function NewsList() {
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "四虎视频-影视资讯-最新影视新闻",
    description:
      "四虎视频影视资讯频道提供最新影视新闻、深度影评和新片预告，覆盖行业动态与热门作品分析，带你第一时间了解影视圈的最新动向。",
    keywords: "影视资讯,影视新闻,最新电影资讯,深度影评,新片预告,四虎视频资讯",
  });

  const filtered = useMemo(
    () => (category === "全部" ? news : news.filter((n) => n.category === category)),
    [category],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "影视资讯" }]} />

      <div className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          影视资讯
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          汇集最新影视新闻、深度影评与热门预告，读懂影视圈正在发生的事。
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setCategory(cat);
              setPage(1);
            }}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              category === cat
                ? "bg-primary-600 text-background-50"
                : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {paged.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {paged.map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="ri-newspaper-line"
            title="暂无相关资讯"
            description="当前分类下还没有内容，建议返回首页看看其他板块，或切换其他资讯分类。"
            actionLabel="重置分类"
            onAction={() => {
              setCategory("全部");
              setPage(1);
            }}
          />
        )}
      </div>

      <Pagination current={currentPage} total={totalPages} onChange={setPage} />
    </div>
  );
}